import React, { Fragment, useState, useRef, useEffect } from 'react';
import { PictureFilled } from '@ant-design/icons';
import { useController } from 'react-hook-form';
import { Upload, Modal, Row, Col, Flex } from 'antd';
import { Required, RowComponent } from '../../../styles/global-style';
import { TextXSMall, TextInputLabel } from '../../text';
import { renderTypeError } from '../index.jsx';
import { closeLoading, openLoading } from '../../alert/hooks/useAlert';
import heic2any from 'heic2any';
import { color } from '../../../resources';

const UploadFilesPic = ({ control, item, setValue, getValues }) => {
  const { rules, name, defaultValue, label, disabled, dataCy, ...propsInput } = item;
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');

  const { field, fieldState } = useController({
    control,
    name,
    rules,
    defaultValue,
  });
  const { error } = fieldState;
  const { value } = field;

  /**
   * Convert HEIF/HEIC images to JPEG before processing
   * @param {File} file - The image file to convert if needed
   * @returns {Promise<File>} - A promise that resolves to the converted file
   */
  const convertHeicToJpeg = async (file) => {
    if (
      file.name.toLowerCase().endsWith('.heic') ||
      file.name.toLowerCase().endsWith('.heif') ||
      file.type === 'image/heic' ||
      file.type === 'image/heif'
    ) {
      try {
        const jpegBlob = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.8,
        });

        const originalName = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
        const jpegFile = new File([jpegBlob], `${originalName}.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });

        return jpegFile;
      } catch (error) {
        console.error('Error converting HEIC:', error);
        return file;
      }
    }
    return file;
  };

  const resizeImage = async (file, maxSizeKB = 250) => {
    if (file.size <= maxSizeKB * 1024 && file.type === 'image/jpeg') {
      return file;
    }

    return new Promise((resolve) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.src = e.target.result;

        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          let quality = 0.9;
          const maxSize = maxSizeKB * 1024;

          const resizeAndCheck = () => {
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            if (file.type !== 'image/jpeg') {
              ctx.fillStyle = '#FFFFFF';
              ctx.fillRect(0, 0, width, height);
            }
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
              (blob) => {
                if (blob.size <= maxSize) {
                  const originalName =
                    file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
                  const resizedFile = new File([blob], `${originalName}.jpg`, {
                    type: 'image/jpeg',
                    lastModified: Date.now(),
                  });
                  resolve(resizedFile);
                } else {
                  if (quality > 0.5) {
                    quality -= 0.1;
                    resizeAndCheck();
                  } else {
                    const scaleFactor = Math.sqrt(maxSize / blob.size) * 0.9;
                    width = Math.floor(width * scaleFactor);
                    height = Math.floor(height * scaleFactor);
                    resizeAndCheck();
                  }
                }
              },
              'image/jpeg',
              quality,
            );
          };

          resizeAndCheck();
        };
      };

      reader.readAsDataURL(file);
    });
  };

  const onChange = async (event) => {
    try {
      const file = event.fileList[0]?.originFileObj;
      if (!file) return;

      openLoading();
      const convertedFile = await convertHeicToJpeg(file);
      const resizedFile = await resizeImage(convertedFile);
      const base64 = await getBase64(resizedFile);
      const newFile = {
        uid: `-${Date.now()}`,
        name: resizedFile.name,
        status: 'done',
        url: base64,
        originFileObj: resizedFile,
      };
      setValue(name, [newFile]);
      closeLoading();
    } catch (error) {
      closeLoading();
      showErrorModal('อัปโหลดไฟล์ไม่ได้', 'กรุณาเลือกไฟล์รูปภาพที่รองรับเท่านั้น (JPG, PNG, HEIC)');
    }
  };
  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (err) => reject(err);
    });

  const handleCancel = () => setPreviewOpen(false);

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
    setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf('/') + 1));
  };

  const uploadStyle = {
    uploadBox: {
      width: '35vw',
      height: '35vw',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      border: '1px dashed #d9d9d9',
      borderRadius: '8px',
      backgroundColor: '#fafafa',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s',
      margin: '10px auto',
      padding: '10px',
      '&:hover': {
        borderColor: color.yellowWhy,
        backgroundColor: '#f0f5ff',
      },
    },
    iconContainer: {
      fontSize: '36px',
      color: color.yellowWhy,
      marginBottom: '4px',
    },
    text: {
      color: '#666',
      fontSize: '14px',
    },
    preview: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '& img': {
        maxWidth: '100%',
        maxHeight: '100%',
      },
    },
  };

  const showErrorModal = (title, content) => {
    Modal.error({
      title: <span style={{ fontFamily: 'Prompt, sans-serif' }}>{title}</span>,
      content: <span style={{ fontFamily: 'Prompt, sans-serif' }}>{content}</span>,
    });
  };
  return (
    <Fragment>
      <RowComponent>
        {label && <TextInputLabel text={label} />}
        {rules && rules.required && <Required>*</Required>}
      </RowComponent>

      <Row gutter={24}>
        {/* File Upload Box */}
        <Col span={12}>
          <Upload
            id={name}
            beforeUpload={(file) => {
              const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/heic'];
              const allowedExtensions = ['.jpg', '.jpeg', '.png', '.heic'];
              const isAccepted =
                allowedTypes.includes(file.type) &&
                allowedExtensions.some((ext) => file.name.toLowerCase().endsWith(ext));

              if (!isAccepted || file.name.toLowerCase().endsWith('.jfif')) {
                showErrorModal(
                  'อัปโหลดไฟล์ไม่ได้',
                  'กรุณาเลือกไฟล์รูปภาพที่รองรับเท่านั้น (JPG, PNG, HEIC)',
                );
                return false;
              }

              return true;
            }}
            listType="picture"
            disabled={disabled || false}
            maxCount={1}
            accept="image/*"
            onPreview={handlePreview}
            fileList={getValues(name)}
            onChange={onChange}
            showUploadList={false}
            {...propsInput}
          >
            <div style={uploadStyle.uploadBox} className="hover:border-blue-400 hover:bg-blue-50">
              <div style={uploadStyle.iconContainer}>
                <PictureFilled style={{ fontSize: '36px', color: color.yellowWhy }} />
              </div>
              <div style={uploadStyle.text}>แนบไฟล์</div>
              <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                เลือกไฟล์จากอุปกรณ์
              </div>
            </div>
          </Upload>
        </Col>
      </Row>

      {/* Preview Area */}
      {value?.length > 0 && (
        <Flex style={uploadStyle.preview} justify="center" align="center">
          <Upload
            listType="picture-card"
            fileList={getValues(name)}
            onPreview={handlePreview}
            onRemove={() => setValue(name, [])}
            showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
            style={{ width: '100%', height: '100%' }}
          />
        </Flex>
      )}

      {/* Preview Modal */}
      <Modal open={previewOpen} title={previewTitle} footer={null} onCancel={handleCancel}>
        <img alt="preview" style={{ width: '100%' }} src={previewImage} />
      </Modal>

      {error && (
        <div style={{ marginTop: '8px' }}>
          <TextXSMall text={renderTypeError(item, error)} color="red" />
        </div>
      )}
    </Fragment>
  );
};

export const MemoizedUploadFilesPic = React.memo(UploadFilesPic);
